import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    InputAdornment,
    IconButton
} from '@mui/material';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons'; // O i tuoi componenti icone
import { CURRENT_SPORT, KEY_MOMENTS_SORTABLE_FIELDS, SUPPORTED_CONTENTS } from "../../constants/globalConstants";
import {useSelectedCard} from "../../contexts/SelectedCardContext";
import { transparentIconButtonSx } from "../../styles/iconHover";
import { isSortedBy, sortObjects } from "../../utils/filterUtils";

const SORT_ACTION_COOLDOWN_MS = 2000;

const CustomSortingSelector: React.FC<{ contentType: string, bigSize?: boolean, onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void }> = ({ contentType, bigSize=true, onSort}) => {
    const {selectedCard, setSelectedCard, sortOptions, setSortOptions} = useSelectedCard()
    const [isCoolingDown, setIsCoolingDown] = useState(false);
    const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (cooldownTimeoutRef.current) clearTimeout(cooldownTimeoutRef.current);
    }, []);

    const triggerSortCooldown = () => {
        setIsCoolingDown(true);
        if (cooldownTimeoutRef.current) clearTimeout(cooldownTimeoutRef.current);
        cooldownTimeoutRef.current = setTimeout(() => setIsCoolingDown(false), SORT_ACTION_COOLDOWN_MS);
    };

    const selectedSortBy = isSortedBy(selectedCard, sortOptions.sortBy, sortOptions.sortOrder)
        ? sortOptions.sortBy
        : '';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box minWidth={(bigSize)? 200:150}>
                <FormControl size="small" sx={{ width: (bigSize)? 200:150 }}>
                    <InputLabel
                        id="sort-select-label"
                        sx={{
                            transform: 'translate(14px, 12px) scale(1)',
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -9px) scale(0.75)',
                            },
                        }}
                    >
                        {'Sort by'}
                    </InputLabel>

                    <Select
                        IconComponent={() => null}
                        labelId="sort-select-label"
                        value={selectedSortBy}
                        label="Sort by"
                        disabled={isCoolingDown}
                        onChange={(e) => {
                            const sortBy = e.target.value as string;
                            const nextSortOptions = {...sortOptions, sortBy};
                            setSortOptions({
                                ...nextSortOptions
                            });
                            setSelectedCard(sortObjects(selectedCard, sortBy, sortOptions.sortOrder));
                            onSort?.(sortBy, sortOptions.sortOrder);
                            triggerSortCooldown();
                        }}
                        input={
                            <OutlinedInput
                                endAdornment={
                                    <InputAdornment position="end" sx={{ ml: 0 }}>
                                        <IconButton
                                            size="large"
                                            disabled={isCoolingDown}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const sortOrder = sortOptions.sortOrder === 'asc' ? 'desc' : 'asc';
                                                const nextSortOptions = {...sortOptions, sortOrder};
                                                setSortOptions({
                                                    ...nextSortOptions
                                                });
                                                if (sortOptions.sortBy) {
                                                    setSelectedCard(sortObjects(selectedCard, sortOptions.sortBy, sortOrder));
                                                }
                                                onSort?.(sortOptions.sortBy || '', sortOrder);
                                                triggerSortCooldown();
                                            }}
                                            sx={{
                                                ...transparentIconButtonSx(),
                                                width: 'auto',
                                                height: 'auto',
                                                minWidth: 0,
                                                border: '0px solid transparent',
                                            }}
                                        >
                                            {sortOptions.sortOrder === "desc" ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                sx={{
                                    pr: 1,
                                    '& .MuiOutlinedInput-input': {
                                        overflow: 'visible',
                                        textOverflow: 'unset',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            />
                        }
                    >
                        {contentType === SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT 
                        ? KEY_MOMENTS_SORTABLE_FIELDS[CURRENT_SPORT].highlight.map((el) => (
                            <MenuItem key={el.id} value={el.id}>{el.value}</MenuItem>
                        )) 
                        : KEY_MOMENTS_SORTABLE_FIELDS[CURRENT_SPORT].local.map((el) => (
                            <MenuItem key={el.id} value={el.id}>{el.value}</MenuItem>
                        )) }
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
};

export default CustomSortingSelector;